import { ReactNode, useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Dialog, DialogContent, Divider, Grid, IconButton, Stack, Typography } from '@mui/material';
import { Add } from 'iconsax-react';
import api from 'api/api';
import serverCall from 'api/serverCall';
import { PopupTransition } from 'components/@extended/Transitions';
import { TickerTapeStockInfo } from 'types/custom/stock';
import { CurrencyType } from 'enum/currency';
import { formatNumberToCurrency } from 'utils/formatters';

export interface StockDetailState {
  open: boolean;
  stockId: string;
}

export const emptyStockDetailState: StockDetailState = { open: false, stockId: '' };

const formatNumber = (value: number | null | undefined, digits = 2) =>
  value == null || Number.isNaN(value) ? '--' : value.toFixed(digits);

const formatRupees = (value: number | null | undefined, shortForm = false) =>
  value == null || Number.isNaN(value) ? '--' : formatNumberToCurrency(value, true, CurrencyType.INR, shortForm);

// Tickertape reports marketCap in ₹ crore already, so it is formatted separately from prices.
const formatMarketCap = (value: number | null | undefined) =>
  value == null || Number.isNaN(value) ? '--' : `₹ ${formatNumberToCurrency(value, false, CurrencyType.INR)} Cr`;

const formatCount = (value: number | null | undefined) => (value == null || Number.isNaN(value) ? '--' : value.toLocaleString('en-IN'));

const formatPct = (value: number | null | undefined) => (value == null || Number.isNaN(value) ? '--' : `${value.toFixed(2)}%`);

// Volumes run to hundreds of millions, which is unreadable in full.
const formatVolume = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return '--';
  if (value >= 1e7) return `${(value / 1e7).toFixed(2)} Cr`;
  if (value >= 1e5) return `${(value / 1e5).toFixed(2)} L`;
  return Math.round(value).toLocaleString('en-IN');
};

type StatProps = { label: string; value: string; color?: string; hint?: string };

// A dash alone reads like a loading bug. Where the value is genuinely not reported,
// hint says why, so an empty cell is legible rather than suspicious.
const Stat = ({ label, value, color, hint }: StatProps) => {
  const empty = value === '--';
  return (
    <Grid item xs={6} sm={4}>
      <Box
        sx={{
          height: '100%',
          px: 1.25,
          py: 1,
          borderRadius: 1.5,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
          {label}
        </Typography>
        <Typography variant="subtitle1" sx={{ color: empty ? 'text.disabled' : color, fontWeight: empty ? 400 : 600, lineHeight: 1.5 }}>
          {value}
        </Typography>
        {empty && hint && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: 10, lineHeight: 1.3 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Grid>
  );
};

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, fontSize: 10 }}
  >
    {children}
  </Typography>
);

type Props = {
  state: StockDetailState;
  setState: (val: StockDetailState) => void;
};

export default function StockDetailModal({ state, setState }: Props) {
  const [details, setDetails] = useState<TickerTapeStockInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => setState(emptyStockDetailState);

  useEffect(() => {
    if (!state.open || !state.stockId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetails(null);

    serverCall.get<TickerTapeStockInfo>(api.getTickerTapeStockInfo(state.stockId), (response, err) => {
      if (cancelled) return;
      setLoading(false);
      if (err) {
        const message = (err.response?.data as any)?.message ?? (err.response?.data as any)?.error;
        setError(message || `Could not load details for ${state.stockId}.`);
      } else {
        setDetails(response);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [state.open, state.stockId]);

  const info = details?.info;
  const ratios = details?.ratios;
  const gic = details?.gic;

  // Where the current price sits inside the 52 week band — the quickest read on the stock.
  const week52Position =
    ratios?.lastPrice != null && ratios?.week52High != null && ratios?.week52Low != null && ratios.week52High > ratios.week52Low
      ? ((ratios.lastPrice - ratios.week52Low) / (ratios.week52High - ratios.week52Low)) * 100
      : null;

  // Negative book value makes ROE and P/B meaningless rather than merely missing.
  const negativeEquity = ratios?.bps != null && ratios.bps < 0;
  const roeHint = negativeEquity ? 'undefined on negative equity' : 'not reported';
  const bookValueColor = negativeEquity ? 'error.main' : undefined;
  const pbColor = ratios?.pb != null && ratios.pb < 0 ? 'error.main' : undefined;
  // Cheaper than its industry is the read that matters at a glance.
  const peVsIndustryColor =
    ratios?.pe != null && ratios?.indpe != null ? (ratios.pe < ratios.indpe ? 'success.main' : 'error.main') : undefined;

  return (
    <Dialog
      open={state.open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      TransitionComponent={PopupTransition}
      aria-labelledby="stock-detail-title"
      // The order request drawer sits at 2001, so the dialog has to clear it.
      sx={{ zIndex: 9998 }}
    >
      <DialogContent sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="h4" id="stock-detail-title">
              {info?.name ?? state.stockId}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              {info?.ticker && (
                <Typography variant="caption" color="text.secondary">
                  {info.ticker}
                </Typography>
              )}
              {info?.exchange && <Chip label={info.exchange} size="small" variant="light" color="info" />}
              {ratios?.marketCapLabel && <Chip label={ratios.marketCapLabel} size="small" variant="light" color="primary" />}
            </Stack>
          </Box>
          <IconButton color="secondary" sx={{ p: 0 }} onClick={close} aria-label="close">
            <Add size={26} style={{ transform: 'rotate(45deg)' }} />
          </IconButton>
        </Stack>

        <Divider sx={{ mt: 2, mb: 2 }} />

        {loading && (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {!loading && error && <Alert severity="warning">{error}</Alert>}

        {!loading && !error && details && (
          <Stack spacing={2.5}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                <Typography variant="h2" sx={{ fontWeight: 700, lineHeight: 1 }}>
                  {formatRupees(ratios?.lastPrice)}
                </Typography>
                {ratios?.week4Pct != null && (
                  <Chip
                    size="small"
                    variant="light"
                    color={ratios.week4Pct >= 0 ? 'success' : 'error'}
                    label={`${ratios.week4Pct >= 0 ? '▲' : '▼'} ${formatPct(Math.abs(ratios.week4Pct))} 4w`}
                  />
                )}
                {ratios?.week52Pct != null && (
                  <Chip
                    size="small"
                    variant="light"
                    color={ratios.week52Pct >= 0 ? 'success' : 'error'}
                    label={`${ratios.week52Pct >= 0 ? '▲' : '▼'} ${formatPct(Math.abs(ratios.week52Pct))} 52w`}
                  />
                )}
              </Stack>

              {ratios?.week52Low != null && ratios?.week52High != null && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      position: 'relative',
                      height: 8,
                      borderRadius: 4,
                      background: (theme) =>
                        `linear-gradient(90deg, ${theme.palette.error.light} 0%, ${theme.palette.warning.light} 50%, ${theme.palette.success.light} 100%)`
                    }}
                  >
                    {week52Position != null && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${Math.min(100, Math.max(0, week52Position))}%`,
                          top: '50%',
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: 'background.paper',
                          border: '3px solid',
                          borderColor: 'primary.main',
                          transform: 'translate(-50%, -50%)',
                          boxShadow: 1
                        }}
                      />
                    )}
                  </Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                    <Typography variant="caption" color="text.secondary">
                      52w low <b>{formatRupees(ratios.week52Low)}</b>
                    </Typography>
                    {week52Position != null && (
                      <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                        {week52Position.toFixed(0)}% of range
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      52w high <b>{formatRupees(ratios.week52High)}</b>
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Box>

            <Stack spacing={1.25}>
              <SectionLabel>Valuation</SectionLabel>
              <Grid container spacing={1.25}>
                <Stat label="Market Cap" value={formatMarketCap(ratios?.marketCap)} />
                <Stat label="P/E" value={formatNumber(ratios?.pe)} hint="needs positive earnings" />
                <Stat label="TTM P/E" value={formatNumber(ratios?.ttmPe)} hint="needs positive earnings" />
                <Stat label="P/B" value={formatNumber(ratios?.pb)} color={pbColor} />
                <Stat label="EPS" value={formatRupees(ratios?.eps)} />
                <Stat label="Book Value" value={formatRupees(ratios?.bps)} color={bookValueColor} />
              </Grid>
            </Stack>

            <Stack spacing={1.25}>
              <SectionLabel>Returns &amp; risk</SectionLabel>
              <Grid container spacing={1.25}>
                <Stat label="ROE" value={formatPct(ratios?.roe)} hint={roeHint} />
                <Stat label="Dividend Yield" value={formatPct(ratios?.divYield)} hint="pays no dividend" />
                <Stat label="Beta" value={formatNumber(ratios?.beta)} />
                <Stat label="Volatility (12m)" value={formatPct(ratios?.volatility12m)} />
                <Stat label="Avg Volume (3m)" value={formatVolume(ratios?.avgVolume3m)} />
                <Stat label="Shareholders" value={formatCount(ratios?.nShareholders)} />
              </Grid>
            </Stack>

            <Stack spacing={1.25}>
              <SectionLabel>Versus industry</SectionLabel>
              <Grid container spacing={1.25}>
                <Stat label="Industry P/E" value={formatNumber(ratios?.indpe)} color={peVsIndustryColor} />
                <Stat label="Industry P/B" value={formatNumber(ratios?.indpb)} />
                <Stat label="Industry Div Yield" value={formatPct(ratios?.inddy)} />
              </Grid>
            </Stack>

            <Divider />

            <Stack spacing={0.5}>
              {info?.sector && (
                <Typography variant="caption" color="text.secondary">
                  Sector: <b>{info.sector}</b>
                </Typography>
              )}
              {gic?.industry && (
                <Typography variant="caption" color="text.secondary">
                  Industry: <b>{gic.industry}</b>
                  {gic.subindustry ? ` — ${gic.subindustry}` : ''}
                </Typography>
              )}
              {details.isin && (
                <Typography variant="caption" color="text.secondary">
                  ISIN: <b>{details.isin}</b>
                </Typography>
              )}
            </Stack>

            {details.labels && Object.keys(details.labels).length > 0 && (
              <Stack spacing={1}>
                {Object.entries(details.labels).map(([key, label]) => (
                  <Box key={key}>
                    <Typography variant="subtitle2">{label.title}</Typography>
                    {label.description && (
                      <Typography variant="caption" color="text.secondary">
                        {label.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}

            {info?.description && (
              <>
                <Divider />
                <Typography variant="caption" color="text.secondary">
                  {info.description}
                </Typography>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
